import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Controller, useFormContext } from 'react-hook-form';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';

const DEFAULT_MIN_CHARS = 3;
const DEFAULT_DEBOUNCE_MS = 300;
const DEFAULT_STALE_TIME = 10 * 60 * 1000;
const persistentOptionCache = new Map();
const getOptionValueKey = (value) => String(value);
const getCacheScopeKey = (name, queryKey) => {
  const [baseQueryKey = 'default'] = queryKey || [];
  return `${name}:${String(baseQueryKey)}`;
};

export default function RHFAsyncAutocomplete({
  name,
  label,
  multiple = false,
  queryKey,
  fetchOptions,
  paginated = false,
  initialOptions = [],
  excludedValues = [],
  minChars = DEFAULT_MIN_CHARS,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  staleTime = DEFAULT_STALE_TIME,
  noOptionsTextIdle = 'Digite para pesquisar',
  noOptionsTextEmpty = 'Não encontrado',
  clearOnSelect = true,
  size = 'small',
  fullWidth = true,
  ...autocompleteProps
}) {
  const { control } = useFormContext();

  const [inputValue, setInputValue] = useState('');
  // Separado do inputValue: com clearOnSelect={false} o campo exibe o nome do selecionado, e
  // usá-lo como busca troca a lista inicial por homônimos do próprio item (que é filtrado).
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const cacheScopeKey = useMemo(() => getCacheScopeKey(name, queryKey), [name, queryKey]);
  const optionCache = useMemo(() => {
    if (persistentOptionCache.has(cacheScopeKey)) {
      return persistentOptionCache.get(cacheScopeKey);
    }

    const cache = new Map();
    persistentOptionCache.set(cacheScopeKey, cache);
    return cache;
  }, [cacheScopeKey]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, debounceMs]);

  const normalizedSearch = debouncedSearch.toLowerCase();
  const shouldFetch = normalizedSearch.length >= minChars;

  const { data: queryOptions = [], isFetching: isFetchingOptions } = useQuery({
    queryKey: [...queryKey, normalizedSearch],
    queryFn: () => fetchOptions(normalizedSearch),
    enabled: shouldFetch && !paginated,
    staleTime,
    placeholderData: [],
  });

  const {
    data: paginatedData,
    fetchNextPage,
    hasNextPage,
    isFetching: isFetchingPaginatedOptions,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [...queryKey, normalizedSearch],
    queryFn: ({ pageParam = 1 }) => fetchOptions(normalizedSearch, pageParam),
    enabled: shouldFetch && paginated,
    staleTime,
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      const loadedOptions = pages.flatMap((page) => page.options || []);
      const loadedOptionValues = new Set(
        loadedOptions.map((option) => getOptionValueKey(option.value))
      );

      return loadedOptionValues.size < lastPage.count ? pages.length + 1 : undefined;
    },
  });

  const options = useMemo(() => {
    if (!paginated) return queryOptions;

    const optionsByValue = new Map();

    paginatedData?.pages?.forEach((page) => {
      page.options?.forEach((option) => {
        const optionValueKey = getOptionValueKey(option.value);

        if (!optionsByValue.has(optionValueKey)) {
          optionsByValue.set(optionValueKey, option);
        }
      });
    });

    return Array.from(optionsByValue.values());
  }, [paginated, paginatedData, queryOptions]);

  const isFetching = paginated ? isFetchingPaginatedOptions : isFetchingOptions;

  const { ListboxProps: listboxProps, ...otherAutocompleteProps } = autocompleteProps;

  const handleListboxScroll = (event) => {
    listboxProps?.onScroll?.(event);

    if (!paginated || !hasNextPage || isFetchingNextPage) return;

    const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
    const isNearListEnd = scrollHeight - scrollTop - clientHeight <= 32;

    if (isNearListEnd) {
      fetchNextPage();
    }
  };

  useEffect(() => {
    options.forEach((option) => {
      optionCache.set(getOptionValueKey(option.value), option);
    });
  }, [options, optionCache]);

  useEffect(() => {
    initialOptions.forEach((option) => {
      optionCache.set(getOptionValueKey(option.value), option);
    });
  }, [initialOptions, optionCache]);

  const noOptionsText = normalizedSearch.length < minChars ? noOptionsTextIdle : noOptionsTextEmpty;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const selectedValues = field.value ?? (multiple ? [] : null);
        const selectedValueList = [];

        if (multiple) {
          selectedValueList.push(...(selectedValues || []));
        } else if (selectedValues != null) {
          selectedValueList.push(selectedValues);
        }

        const hiddenValueSet = new Set(
          [...selectedValueList, ...excludedValues].map((value) => getOptionValueKey(value))
        );

        // Filtra na exibição, não no fetchOptions: o getNextPageParam compara as opções
        // carregadas com o count do backend, e tirar item da página faria pedir página inexistente.
        const filteredOptions = options.filter(
          (option) => !hiddenValueSet.has(getOptionValueKey(option.value))
        );

        const selectedOptions = multiple
          ? (selectedValues || [])
            .map((value) => optionCache.get(getOptionValueKey(value)))
            .filter(Boolean)
          : optionCache.get(getOptionValueKey(selectedValues)) || null;

        return (
          <Autocomplete
            multiple={multiple}
            size={size}
            fullWidth={fullWidth}
            options={filteredOptions}
            value={selectedOptions}
            onChange={(_, newValue) => {
              if (multiple) {
                field.onChange(newValue.map((option) => option.value));
              } else {
                field.onChange(newValue ? newValue.value : null);
              }

              if (clearOnSelect) {
                setInputValue('');
                setSearchTerm('');
              } else if (!multiple) {
                setInputValue(newValue?.label || '');
                setSearchTerm('');
              }
            }}
            inputValue={inputValue}
            onInputChange={(_, value, reason) => {
              if (reason === 'input') {
                setInputValue(value);
                setSearchTerm(value);
              }
            }}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            getOptionLabel={(option) => option.label || ''}
            loading={isFetching}
            noOptionsText={noOptionsText}
            ListboxProps={{
              ...listboxProps,
              onScroll: handleListboxScroll,
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isFetching ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            {...otherAutocompleteProps}
          />
        );
      }}
    />
  );
}

RHFAsyncAutocomplete.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  multiple: PropTypes.bool,
  queryKey: PropTypes.arrayOf(PropTypes.any).isRequired,
  fetchOptions: PropTypes.func.isRequired,
  paginated: PropTypes.bool,
  initialOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  excludedValues: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  minChars: PropTypes.number,
  debounceMs: PropTypes.number,
  staleTime: PropTypes.number,
  noOptionsTextIdle: PropTypes.string,
  noOptionsTextEmpty: PropTypes.node,
  clearOnSelect: PropTypes.bool,
  size: PropTypes.string,
  fullWidth: PropTypes.bool,
};
